import { routes, SKETCH_WEBSITE } from '@sketch/constants'
import { InitialUserFragment } from '@sketch/gql-types'
import {
  useBreakpoint,
  Button,
  Link,
  ExternalLink,
  MyWorkspacePill,
} from '@sketch/components'
import {
  WorkspaceProfileResultLoading,
  WorkspaceProfileResultSuccess,
} from 'modules/workspaceProfile/hooks/useGetWorkspaceProfile'
import React from 'react'

import {
  CenterBannerWrapper,
  FullWidthBannerWrapper,
  Header,
  LeftSideLogoWrapper,
  Logo,
  RightSideButtonWrapper,
} from './PublicProfileHeader.styles'
import { PublicViewHiddenProfileBanner } from './PublicViewHiddenProfileBanner'

export interface PublicProfileHeaderProps {
  workspaceId: string
  profileResult: WorkspaceProfileResultSuccess | WorkspaceProfileResultLoading
  user: Pick<InitialUserFragment, 'name' | 'avatar'> | undefined
}

interface ShareYourCreationsButtonProps {
  dimmed: boolean
}

const ShareYourCreationsButton = (props: ShareYourCreationsButtonProps) => {
  const { dimmed } = props
  return (
    <Button small variant={dimmed ? 'tertiary' : 'primary-untinted'}>
      Share Your Creations
    </Button>
  )
}

interface LogoLinkProps extends Pick<PublicProfileHeaderProps, 'user'> {}

const LogoLink = (props: LogoLinkProps) => {
  const { user } = props

  if (user) {
    // There idea is to redirect user to the home page, i.e. www.sketch.com marketing website
    // However, if user is signed in, www.sketch.com will redirect the user back to the web app
    // (using `/c` path), so we are just making this process much faster:
    return (
      <Link to={{ pathname: routes.ENTRY.create({}) }} title="Home">
        <Logo />
      </Link>
    )
  }

  return (
    <ExternalLink href={SKETCH_WEBSITE} title="Home">
      <Logo />
    </ExternalLink>
  )
}

export const PublicProfileHeader = (props: PublicProfileHeaderProps) => {
  const { user, workspaceId, profileResult } = props

  const isLargeDesktop = useBreakpoint('lg')
  const isSmall = !useBreakpoint('sm')

  const showProfileHiddenBanner =
    user &&
    profileResult.profile?.workspace.userRole === 'ADMIN' &&
    profileResult.profile.isPublic === false

  return (
    <>
      {!isLargeDesktop && showProfileHiddenBanner ? (
        <FullWidthBannerWrapper>
          <PublicViewHiddenProfileBanner
            workspaceId={workspaceId}
            rounded={false}
          />
        </FullWidthBannerWrapper>
      ) : null}
      <Header>
        <LeftSideLogoWrapper>
          <LogoLink user={user} />
        </LeftSideLogoWrapper>

        {/**
         * We are using Flex here (and in sibling components) because we want to center the banner within
         * the center of the screen and not in the center between two sibling components.
         * As it just happens, that two sibling components are of different width.
         * Also we want to keep the banner in a single line when it is rendered here
         * */}
        {isLargeDesktop && showProfileHiddenBanner ? (
          <CenterBannerWrapper>
            <PublicViewHiddenProfileBanner workspaceId={workspaceId} />
          </CenterBannerWrapper>
        ) : null}

        <RightSideButtonWrapper>
          {user ? (
            <Link to={{ pathname: routes.ENTRY.create({}) }}>
              <MyWorkspacePill user={user} />
            </Link>
          ) : (
            <ExternalLink href={SKETCH_WEBSITE}>
              <ShareYourCreationsButton dimmed={isSmall} />
            </ExternalLink>
          )}
        </RightSideButtonWrapper>
      </Header>
    </>
  )
}
